import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { EXHUT_WEB_LOCAL_STORAGE_TOKEN } from "../utils/constants";

interface IUpdateAccountPayload {
  plaidToken?: string;
  name?: string;
}

// uses same endpoint as useUpdatePlaidAccountToken, but that takes token payload to update plaid token at backend
export function useUpdateAccount(accountId?: string) {
  return _usePatch<any, any, IUpdateAccountPayload>({
    path: `accounts/${accountId}`,
    keys: ["accounts", accountId!],
  });
}

interface IUpdateSavingsPayload {
  description?: string;
  amount?: number;
  savingsDate: string;
  shared?: boolean;
  users?: string[];
  savingsId: string;
}

export function useUpdateSavings(groupId?: string, savingsId?: string) {
  return _usePatch<any, any, IUpdateSavingsPayload>({
    path: `groups/${groupId}/savings/:savingsId`,
    keys: ["groups", groupId!, "savings", savingsId!],
    replacePathParams: "savingsId",
  });
}

interface IUpdateSavingsGoalPayload {
  name?: string;
  targetAmount?: number;
  targetDate?: string;
  users?: string[];
  goalId: string;
}

export function useUpdateSavingsGoal(groupId?: string) {
  return _usePatch<any, any, IUpdateSavingsGoalPayload>({
    path: `groups/${groupId}/savings-goals/:goalId`,
    keys: ["groups", groupId!, "savings-goals"],
    replacePathParams: "goalId",
  });
}

interface IUpdateSavingsGoalItemPayload {
  amount?: number;
  itemDate?: string;
  note?: string;
  itemId: string;
}

export function useUpdateSavingsGoalItem(groupId?: string, goalId?: string) {
  return _usePatch<any, any, IUpdateSavingsGoalItemPayload>({
    path: `groups/${groupId}/savings-goals/${goalId}/items/:itemId`,
    keys: ["groups", groupId!, "savings-goals", goalId!, "items"],
    replacePathParams: "itemId",
  });
}

interface IUpdateIncomePayload {
  // Always the annual, pre-tax amount.
  amount?: number;
  name?: string;
  users?: string[];
  incomeId: string;
}

export function useUpdateIncome(groupId?: string) {
  return _usePatch<any, any, IUpdateIncomePayload>({
    path: `groups/${groupId}/incomes/:incomeId`,
    keys: ["groups", groupId!, "incomes"],
    replacePathParams: "incomeId",
  });
}

export interface IUpdateProfilePayload {
  firstName?: string;
  lastName?: string;
  countryCode?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export function useUpdateProfile() {
  return _usePut<any, any, IUpdateProfilePayload>({
    path: `users/profile`,
    keys: ["users", "profile"],
    resetKeys: [],
    host: process.env.REACT_APP_AUTH_BE_URL,
  });
}

interface IUpdateProfilePhonePayload {
  phone: string;
}

// Phone lives behind a dedicated endpoint; the /users/profile route rejects it.
export function useUpdateProfilePhone() {
  return _usePut<any, any, IUpdateProfilePhonePayload>({
    path: `users/profile/phone`,
    keys: ["users", "profile"],
    resetKeys: [],
    host: process.env.REACT_APP_AUTH_BE_URL,
  });
}

export function _usePut<T1, T2, T3>({
  path,
  keys,
  resetKeys,
  host,
  replacePathParams,
}: {
  path: string;
  keys?: string[];
  resetKeys?: string[];
  host?: string;
  replacePathParams?: string;
}) {
  const _host = host ?? process.env.REACT_APP_BE_URL;
  let _url = `${_host}/${path}`;

  let headers: any = {
    "Content-Type": "application/json",
  };
  const sign = localStorage.getItem(EXHUT_WEB_LOCAL_STORAGE_TOKEN);
  headers["authorization"] = sign;
  headers["bearer"] = sign;

  const queryClient = useQueryClient();

  return useMutation<T1, T2, T3>({
    mutationFn: async (req) => {
      if (replacePathParams) {
        _url = _url.replace(
          `:${replacePathParams}`,
          (req as any)[replacePathParams],
        );

        const copy = { ...req };
        // @ts-ignore
        delete copy[replacePathParams];
        req = copy;
      }

      return axios
        .put(_url, req, {
          headers,
        })
        .then((resp) => resp.data);
    },
    mutationKey: keys,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: resetKeys }),
  });
}

interface IUpdateExpensePayload {
  category: string;
  subcategory: string;
  amount: number;
  creditcard: string;
  comments: string;
  expenseDate: string;
}

export function useUpdateExpense(groupId?: string, expenseId?: string) {
  return _usePatch<any, any, IUpdateExpensePayload>({
    path: `groups/${groupId}/expenses/${expenseId}`,
    keys: ["groups", groupId!, "expenses", expenseId!],
  });
}

function _usePatch<T1, T2, T3>({
  path,
  keys,
  resetKeys,
  authenticated = true,
  host,
  replacePathParams,
}: {
  path: string;
  keys?: string[];
  resetKeys?: string[];
  authenticated?: boolean;
  host?: string;
  replacePathParams?: string;
}) {
  let _url = host
    ? `${host}/${[path]}`
    : `${process.env.REACT_APP_BE_URL2}/${path}`;

  let headers: any = {
    "Content-Type": "application/json",
  };

  if (authenticated) {
    const sign = localStorage.getItem(EXHUT_WEB_LOCAL_STORAGE_TOKEN);
    headers["Authorization"] = sign;
  } else {
    headers["Access-Type"] = "offline";
  }

  return useMutation<T1, T2, T3>({
    mutationFn: async (req) => {
      if (replacePathParams) {
        _url = _url.replace(
          `:${replacePathParams}`,
          (req as any)[replacePathParams],
        );

        const copy = { ...req };
        // @ts-ignore
        delete copy[replacePathParams];
        req = copy;
      }
      return axios
        .patch(_url, req, {
          headers,
        })
        .then((resp) => resp.data);
    },
    mutationKey: keys,
  });
}
